import { Ref, useEffect, useMemo, useState } from "react";
import { Map, Typography } from "@/components/atoms";
import { MapHandle } from "@/components/atoms/Map/Map";
import { useIsMdUp } from "@/hooks/useIsMdUp";
import { formatDistance, formatDuration } from "@/lib/journeyEstimate";
import { Route, getRoutePath } from "@/lib/mapbox";
import { useAppSelector } from "@/redux/hooks";
import { selectOrderedLocations, selectPickupDatetime } from "@/redux/slices/clientBooking/selectors";

// The Address panel overlays the map's left side on md+ and the bottom on mobile; pad the
// fit-to-markers on that side so pickup/stops/destination never hide behind the panel.
const DESKTOP_FIT = { top: 64, right: 64, bottom: 64, left: 440 };
const MOBILE_FIT = { top: 64, right: 24, bottom: 360, left: 24 };

/**
 * Full-bleed map behind the booking panels: plots the booking's pickup/stops/destination as
 * purple pins and draws the route polyline between them. The pin picker is a separate
 * modal (PinLocationModal). Layout-only default (Sydney) until locations are set.
 */
export const BookingMapBackground = ({ mapControlRef }: { mapControlRef?: Ref<MapHandle> }) => {
  const allLocations = useAppSelector(selectOrderedLocations);
  /*
   * Only the locations we can plot. A booking whose address was never validated ("Requires
   * Validation") carries no lat/lng, and passing those through produced two failures at once: Google
   * threw InvalidValueError out of setCenter/setPosition — escaping to the error boundary and blanking
   * the sheet — and the directions call went to Mapbox as ".../driving-traffic/,;," which it rejects.
   *
   * Dropping them shows the trip without that leg, which is the honest picture: we do not know where
   * it is. The sheet itself still lists the address in the summary.
   */
  const ordered = useMemo(
    () => allLocations.filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng)),
    [allLocations]
  );
  const [route, setRoute] = useState<Route>({ path: [], distanceMetres: 0, durationSeconds: 0 });
  const routePath = route.path;

  /*
   * The pickup instant, so the duration is the traffic AT THAT TIME rather than right now. On one
   * Sydney route that is 10.8 minutes at 3am against 14.0 in evening peak, and Jodie's whole use for
   * this is advising clients when to be picked up.
   *
   * Null until the client picks a date and time — which is exactly why the design shows distance
   * alone at first and adds the travel time afterwards. There is no honest duration to show before
   * we know when the trip is.
   */
  const pickupDatetime = useAppSelector(selectPickupDatetime);
  const departAt = useMemo(() => {
    if (!pickupDatetime) return undefined;
    const parsed = new Date(pickupDatetime);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [pickupDatetime]);

  /*
   * Anchored to the middle of the drawn line rather than the geographic midpoint of the endpoints:
   * on a route that doglegs, the straight-line middle can sit off the road entirely.
   */
  const badge = useMemo(() => {
    if (routePath.length < 2 || route.distanceMetres <= 0) return null;
    /*
     * Nothing at all when a leg was dropped for having no coordinates. The route drawn is then a
     * SHORTER trip than the one being booked, and a confident "8 KM" under it would be quoting a
     * number we know to be wrong — worse than the blank the client had before this existed.
     */
    if (ordered.length !== allLocations.length) return null;

    return {
      position: routePath[Math.floor(routePath.length / 2)],
      label: (
        /*
          The padding is the GAP: the anchor is this wrapper's bottom edge, which sits on the route,
          so padding below the tail lifts the whole callout clear of the line instead of resting on it.
        */
        <div className="relative pb-2.5">
          <div className="whitespace-nowrap rounded-lg bg-neutral-black px-3 py-1.5 text-center shadow-dropdown">
            {/* Travel time only once we know WHEN — see departAt above. Distance stands alone until then. */}
            {departAt && (
              <Typography variant="action" className="block font-semibold text-white">
                {formatDuration(route.durationSeconds)}
              </Typography>
            )}
            <Typography variant="small" className="block font-normal text-white">
              {formatDistance(route.distanceMetres)}
            </Typography>
          </div>
          {/* A rotated square rather than a border triangle: it inherits the pill's colour in one place. */}
          <div className="absolute bottom-2.5 left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rotate-45 bg-neutral-black" />
        </div>
      ),
    };
  }, [routePath, route.distanceMetres, route.durationSeconds, departAt, ordered.length, allLocations.length]);

  const isMdUp = useIsMdUp();
  const fitPadding = isMdUp ? DESKTOP_FIT : MOBILE_FIT;

  // Stable key so the directions effect only re-fires when the actual coordinates change.
  const routeKey = ordered.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    if (ordered.length < 2) {
      setRoute({ path: [], distanceMetres: 0, durationSeconds: 0 });
      return;
    }
    let cancelled = false;
    getRoutePath(
      ordered.map((p) => ({ lat: p.lat, lng: p.lng })),
      departAt
    ).then((next) => {
      if (!cancelled) setRoute(next);
    });
    return () => {
      cancelled = true;
    };
    // Re-fetch when the pickup time changes too: the same route takes a different time in peak hour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, departAt?.getTime()]);

  return (
    <div className="h-full w-full">
      <Map
        ref={mapControlRef}
        className="!rounded-none"
        zoom={13}
        fitToMarkers
        fitPadding={fitPadding}
        // Anchor center to the first booking location so a late geolocation fix can't recenter the
        // map away from the fitted markers/route; fitToMarkers still frames the whole trip.
        center={ordered.length ? { lat: ordered[0].lat, lng: ordered[0].lng } : undefined}
        markers={ordered.map((p, i) => ({ position: { lat: p.lat, lng: p.lng }, variant: i === 0 ? "dot" : "pin" }))}
        routePath={routePath}
        routeBadge={badge}
      />
    </div>
  );
};
